// @flow
import { Api } from 'core/services/api'
import { decamelize } from 'utils/camelize'
import type {
  UserGetParam,
  ApiUserMe,
  ApiUserProfile,
  ProfileUpdateUser,
  UploadAvatar,
  UserType,
  PossibleAccountFieldsCamel,
} from './types'
import { USER_GET_PARAMS } from './constants'
import { prepareUserProfileData } from './utils'

/**
 * Get user me data
 *
 * @returns {Promise<ApiUserMe>}
 */
export const getUserMe = (): Promise<ApiUserMe> => {
  return Api.get<{||}, ApiUserMe>(`/api/user/${USER_GET_PARAMS[0]}`).then((response) => response.data)
}

/**
 * Get user profile data
 *
 * @returns {Promise<ApiUserProfile>}
 */
export const getUserProfile = (): Promise<ApiUserProfile> => {
  return Api.get<{||}, ApiUserProfile>(`/api/user/${USER_GET_PARAMS[1]}`).then((response) => response.data)
}

/**
 * Get user data with param
 *
 * @param {string} [param='me'] - What to fetch. Can be: ['me', 'profile']
 * @returns {Promise<ApiUser>}
 */
export const get = (param: UserGetParam = USER_GET_PARAMS[0]): Promise<ApiUserMe> | Promise<ApiUserProfile> => {
  if (!USER_GET_PARAMS.includes(param)) {
    throw new Error(`Invalid userApi.get method's parameter. Available values: ${USER_GET_PARAMS.join(', ')}`)
  }

  if (param === USER_GET_PARAMS[0]) {
    return getUserMe()
  }

  return getUserProfile()
}

/** Patch user profile */
export const updateUserProfile = (values: $Shape<PossibleAccountFieldsCamel>): Promise<void> =>
  Api<{ user: ProfileUpdateUser }, void>({
    url: '/api/user/profile',
    method: 'PATCH',
    data: { user: prepareUserProfileData(decamelize(values)) },
  }).then((response) => response.data)

/** Convert user's type */
export const convertUserType = (value: UserType): Promise<void> =>
  Api<{| to_type: UserType |}, void>({
    url: '/api/user/type_conversions',
    method: 'POST',
    data: {
      to_type: value,
    },
  }).then(() => undefined)

/** Confirm email */
export const confirmEmail = (): Promise<void> => Api.post<void, void>('/api/user/confirmation').then(() => undefined)

/** Accept coach agreement */
export const acceptCoachAgreement = (): Promise<void> =>
  Api.post<void, void>('/api/user/coach/agreement').then(() => undefined)

/** Upload avatar */
export const uploadAvatar = (data: FormData): Promise<UploadAvatar> =>
  Api.post<FormData, UploadAvatar>('/api/user/avatar', data).then((response) => response.data)

/** Change user's grade */
export const changeUserGrade = (gradeId: number): Promise<void> =>
  Api.post<{ grade_id: number }, void>('/api/user/grade_checks', { grade_id: gradeId }).then(() => undefined)

/** Change user's school entry year */
export const changeUserSchoolEntryYear = (year: number) =>
  Api.post<{| school_entry_year: number |}, void>('/api/user/school_entry_year', { school_entry_year: year })
